import { PureComponent } from 'react';
import PropTypes from 'prop-types';

const NOOP = () => {};
const log = __DEV__
  ? console.log
  : NOOP;

// Normal closure; the connection successfully completed whatever purpose
//   for which it was created.
const NORMAL_CLOSURE = 1000;
// Indicates that no status code was provided even though one was expected.
const NO_STATUS_RECEIVED = 1005;

export const CLOSE_STATUS = {
  NORMAL_CLOSURE,
  NO_STATUS_RECEIVED,
};

const DEFAULT_SOCKET_TIMEOUT = 3000;
const DEFAULT_SOCKET_RECONNECT_DELAY = 5000;

export function Socket(url, {
  timeout = DEFAULT_SOCKET_TIMEOUT,
  onmessage = NOOP,
  onclose = NOOP,
  onerror = NOOP,
  onopen = NOOP,
  onreconnect = NOOP,
  protocols,
} = {}) {
  let ws;
  let timer;

  const needReconnect = e => e.code !== NORMAL_CLOSURE
    && e.code !== NO_STATUS_RECEIVED
    && !ws.__closeRequested__
    && !timer;

  const $ = {
    onmessage,
    onclose: e => {
      if (needReconnect(e)) this.reconnect(e);
      onclose(e);
    },

    onerror: e => {
      onerror(e);

      if (needReconnect(e)) {
        this.reconnect(e);
      }
    },

    onopen(e) {
      timer = null;
      ws.__closeRequested__ = false;
      onopen(e);
    },
  };

  this.open = () => {
    ws = new WebSocket(url, protocols);
    Object.assign(ws, $);
  };

  this.reconnect = e => {
    timer = setTimeout(() => {
      timer = null;
      onreconnect(e);

      this.open();
    }, timeout);
  };

  this.json = x => {
    ws.send(JSON.stringify(x));
  };

  this.send = x => {
    ws.send(x);
  };

  this.close = (x, y) => {
    ws.__closeRequested__ = true;
    ws.close(x, y);
  };

  this.open(); // init
}

const {
  func,
} = PropTypes;

export default
class AbstractSocketProvider extends PureComponent {
  static propTypes = {
    url: PropTypes.string.isRequired,
    reconnectDelay: PropTypes.number,
    keepAlive: PropTypes.bool,
    parse: func,
    onError: func,
    onReconnect: func,
    onMessage: func,
    onConnected: func,
    onDisconnected: func,
  };

  static defaultProps = {
    reconnectDelay: DEFAULT_SOCKET_RECONNECT_DELAY,
    keepAlive: false,
    parse: JSON.parse,
    onError: NOOP,
    onReconnect: NOOP,
    onMessage: NOOP,
    onConnected: NOOP,
    onDisconnected: NOOP,
  }

  componentDidMount() {
    this._connect();

    // do not attach to `visibilitychange` if `keepAlive` is true
    const {
      keepAlive,
      reconnectDelay,
    } = this.props;

    if (keepAlive) {
      return;
    }

    window.addEventListener('offline', this._offlineListener = () => {
      this._disconnect();
    });

    window.addEventListener('online', this._onlineListener = () => {
      setTimeout(() => {
        this._connect();
      }, reconnectDelay);
    });
  }

  componentDidUpdate() { this._connect(); }

  componentWillUnmount() {
    this._close('component unmounting');

    if (this._onlineListener) {
      document.removeEventListener('online', this._onlineListener);
    }

    if (this._offlineListener) {
      document.removeEventListener('offline', this._offlineListener);
    }
  }

  _onMessage = ({ data = {} }) => {
    const {
      parse,
      onMessage,
    } = this.props;
    const parsed = parse(data);

    if (parsed) {
      onMessage(parsed);
    }
  }


  _connect = () => {
    const {
      url,
      onError,
      onReconnect,
      onConnected,
      onDisconnected,
    } = this.props;

    // if (__DEV__) bog.info('WebSocket connecting:', wssUri, streamNames);
    // invalid market
    if (!url) return;

    this._close('close socket');
    this._socket = new Socket(url, {
      onopen: onConnected,
      onmessage: this._onMessage.bind(this),
      onreconnect: ev => {
        onReconnect(ev);
      },
      onmaximum: () => {
        log(`webSocket max reconnects reached: ${url}`);
      },
      onerror: err => {
        onDisconnected(err);
        onError(err);
      },
    });
  }


  _disconnect = () => {
    this._close('user agent is offline', 1e3);
  }

  _close(message, code = NORMAL_CLOSURE) {
    this.props.onDisconnected();
    if (this._socket) {
      this._socket.close(code, message);
    }
  }

  render() {
    return null;
  }
}
